import React, { useState, useContext, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import api from '../api';
import './Auth.css';
import girlImage from '../assets/girlimage.png';


const Login = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { login, user } = useContext(AuthContext);

  // Determine initial tab based on URL
  const [activeTab, setActiveTab] = useState(location.pathname === '/signup' ? 'signup' : 'login');
  
  // Landing Page flow
  const [loginStep, setLoginStep] = useState('product-selection'); // 'product-selection' or 'auth'
  const [selectedRole, setSelectedRole] = useState('seller');

  const products = [
    { id: 'cotton', name: 'Cotton', icon: '☁️' },
    { id: 'viscose', name: 'Viscose', icon: '🧶' },
    { id: 'yarn', name: 'Yarn', icon: '🧵' },
    { id: 'mmf', name: 'MMF', icon: '🏭' },
  ];

  const handleProductSelect = (productId) => {
    localStorage.setItem('intendedRole', selectedRole);
    localStorage.setItem('intendedProduct', productId);
    setActiveTab('signup'); // Default to signup flow when clicking a product
    setLoginStep('auth');
  };

  // Redirection for already logged-in users
  useEffect(() => {
    if (user) {
      navigate('/role-selection');
    }
  }, [user, navigate]);

  // Login State
  const [loginData, setLoginData] = useState({ email: '', password: '' });

  // Signup State
  const [signupData, setSignupData] = useState({
    name: '',
    email: '',
    mobile: '',
    password: '',
    confirmPassword: '',
    securityPin: '',
    confirmSecurityPin: ''
  });

  // Forgot Password State
  const [forgotPasswordState, setForgotPasswordState] = useState('idle'); // idle, otp-sent, verified, input-email
  const [resetEmail, setResetEmail] = useState('');
  const [resetOtp, setResetOtp] = useState('');
  const [newPassword, setNewPassword] = useState('');

  // UI State
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    if (location.state?.message) {
      setError(location.state.message);
    }
  }, [location.state]);

  const handleLoginChange = (e) => setLoginData({ ...loginData, [e.target.name]: e.target.value });
  const handleSignupChange = (e) => setSignupData({ ...signupData, [e.target.name]: e.target.value });

  const handleLoginSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const response = await api.post('/auth/login', loginData);
      login(response.data.token, response.data.user);
    } catch (err) {
      setError(err.response?.data?.message || 'Login failed. Please check your credentials.');
    } finally {
      setLoading(false);
    }
  };

  const handleSignupSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (signupData.password !== signupData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (signupData.password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    if (!/^\d{4}$/.test(signupData.securityPin)) {
      setError('Security PIN must be exactly 4 digits');
      return;
    }

    if (signupData.securityPin !== signupData.confirmSecurityPin) {
      setError('Security PINs do not match');
      return;
    }

    setLoading(true);
    try {
      const response = await api.post('/auth/register', {
        name: signupData.name,
        email: signupData.email,
        mobile: signupData.mobile,
        password: signupData.password,
        securityPin: signupData.securityPin
      });
      login(response.data.token, response.data.user);
      navigate('/seller/domestic-hvi/details');
    } catch (err) {
      setError(err.response?.data?.message || 'Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Forgot Password Mock Logic
  const handleSendOtp = () => {
    if (!resetEmail) { setError('Please enter your email'); return; }
    // Mock API call
    alert(`OTP sent to ${resetEmail} (Demo: 123456)`);
    setForgotPasswordState('otp-sent');
    setError('');
  };

  const handleVerifyOtp = () => {
    if (resetOtp !== '123456') { setError('Invalid OTP'); return; }
    setForgotPasswordState('verified');
    setError('');
  };

  const handleResetPassword = () => {
    alert('Password reset successfully! Please login.');
    setForgotPasswordState('idle');
    setActiveTab('login');
  };

  return (
    <div className="auth-container">
      {/* Left Side */}
      <div className="auth-left" style={{ padding: 0, position: 'relative', display: 'flex', alignItems: 'flex-end', justifyContent: 'center', overflow: 'hidden' }}>
        <img 
          src={girlImage} 
          alt="Trading" 
          style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center 15%', display: 'block', position: 'absolute', top: 0, left: 0, zIndex: 1 }} 
        />
        <div className="auth-branding" style={{ position: 'relative', zIndex: 2, background: 'rgba(13, 71, 161, 0.82)', padding: '18px 24px', borderRadius: '14px', backdropFilter: 'blur(10px)', border: '1px solid rgba(255, 255, 255, 0.25)', maxWidth: '440px', margin: '15px', marginBottom: '16px', textAlign: 'center' }}>
          <h1 className="auth-welcome-text" style={{ fontSize: '1.65rem', fontWeight: '800', marginBottom: '8px', color: '#ffffff', textShadow: '0 2px 4px rgba(0,0,0,0.3)' }}>Welcome to GoEasyTrade</h1>
          <div className="premium-caption" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px', marginBottom: '8px' }}>
            <span className="caption-line" style={{ height: '2px', width: '25px', background: 'rgba(255, 255, 255, 0.6)' }}></span>
            <p className="auth-tagline highlight" style={{ fontSize: '0.88rem', fontWeight: '700', color: '#e0f2fe', margin: 0, textTransform: 'uppercase', letterSpacing: '1.5px', textShadow: '0 2px 4px rgba(0,0,0,0.3)' }}>Trading is simplified</p>
            <span className="caption-line" style={{ height: '2px', width: '25px', background: 'rgba(255, 255, 255, 0.6)' }}></span>
          </div>
          <p className="auth-description" style={{ color: '#ffffff', fontSize: '0.85rem', lineHeight: '1.45', textShadow: '0 1px 2px rgba(0,0,0,0.3)', margin: 0 }}>
            Experience the future of cotton trading. Connecting verified sellers and buyers with unmatched efficiency.
          </p>
        </div>
      </div>

      {/* Right Side */}
      <div className="auth-right">
        {loginStep === 'product-selection' ? (
          <div className="auth-card-premium" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '20px' }}>
            <div className="auth-header" style={{ marginBottom: '10px' }}>
              <h1 className="auth-logo-text"><span className="logo-main">GoEasyTrade</span></h1>
              <p className="auth-subtitle">Select your role and product to get started</p>
            </div>
            
            <div style={{ width: '100%', maxWidth: '400px' }}>
              <h3 style={{ fontSize: '1rem', color: '#1e293b', marginBottom: '10px' }}>1. I want to be a:</h3>
              <div className="auth-tabs-premium" style={{ marginBottom: '20px' }}>
                <button
                  className={`tab-item ${selectedRole === 'seller' ? 'active' : ''}`}
                  onClick={() => setSelectedRole('seller')}
                >
                  Seller
                </button>
                <button
                  className={`tab-item ${selectedRole === 'buyer' ? 'active' : ''}`}
                  onClick={() => setSelectedRole('buyer')}
                >
                  Buyer
                </button>
              </div>
              
              <h3 style={{ fontSize: '1rem', color: '#1e293b', marginBottom: '10px' }}>2. Select Product Category:</h3>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                {products.map(p => (
                  <button 
                    key={p.id}
                    onClick={() => handleProductSelect(p.id)}
                    style={{
                      background: 'white',
                      border: '1px solid #cbd5e1',
                      padding: '15px',
                      borderRadius: '12px',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: '8px',
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.02)'
                    }}
                    onMouseOver={e => { e.currentTarget.style.borderColor = '#0d47a1'; e.currentTarget.style.boxShadow = '0 4px 12px rgba(13,71,161,0.1)'; }}
                    onMouseOut={e => { e.currentTarget.style.borderColor = '#cbd5e1'; e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.02)'; }}
                  >
                    <span style={{ fontSize: '2rem' }}>{p.icon}</span>
                    <span style={{ fontWeight: 'bold', color: '#1e293b' }}>{p.name}</span>
                  </button>
                ))}
              </div>
            </div>
            
            <div style={{ marginTop: '20px', borderTop: '1px solid #e2e8f0', paddingTop: '20px', width: '100%', textAlign: 'center' }}>
              <p style={{ color: '#64748b', fontSize: '0.9rem', marginBottom: '10px' }}>Already have an account?</p>
              <button 
                type="button" 
                onClick={() => { setActiveTab('login'); setLoginStep('auth'); }}
                className="btn-auth-primary"
                style={{ background: 'transparent', color: '#0d47a1', border: '1px solid #0d47a1' }}
              >
                Sign In Instead
              </button>
            </div>
          </div>
        ) : (
          <div className="auth-card-premium">
            <div className="auth-header">
            <h1 className="auth-logo-text">
              <span className="logo-main">GoEasyTrade</span>
            </h1>
            <p className="auth-subtitle">{activeTab === 'login' ? 'Sign in to your account' : 'Create your business account'}</p>
          </div>

          {/* Tabs */}
          <div className="auth-tabs-premium">
            <button
              className={`tab-item ${activeTab === 'login' ? 'active' : ''}`}
              onClick={() => setActiveTab('login')}
            >
              Sign In
            </button>
            <button
              className={`tab-item ${activeTab === 'signup' ? 'active' : ''}`}
              onClick={() => setActiveTab('signup')}
            >
              Sign Up
            </button>
          </div>

          <div className="auth-form-container">
            {error && <div className="error-banner">{error}</div>}

            {/* Login Form */}
            {activeTab === 'login' && forgotPasswordState === 'idle' && (
              <form onSubmit={handleLoginSubmit} className="premium-form">
                <div className="input-group-premium">
                  <label>Email Address</label>
                  <input type="email" name="email" value={loginData.email} onChange={handleLoginChange} required placeholder="e.g. name@company.com" />
                </div>
                <div className="input-group-premium">
                  <label>Password</label>
                  <div className="password-wrapper">
                    <input type={showPassword ? 'text' : 'password'} name="password" value={loginData.password} onChange={handleLoginChange} required placeholder="••••••••" />
                    <button type="button" className="toggle-pass" onClick={() => setShowPassword(!showPassword)}>
                      {showPassword ? 'Hide' : 'Show'}
                    </button>
                  </div>
                </div>
                <button type="submit" className="btn-auth-primary" disabled={loading}>
                  {loading ? 'Sign In...' : 'Sign In'}
                </button>
                <div className="auth-extra">
                  <button type="button" className="btn-link" onClick={() => setForgotPasswordState('input-email')}>
                    Forgot password?
                  </button>
                </div>
              </form>
            )}

            {/* Forgot Password Flow */}
            {activeTab === 'login' && forgotPasswordState !== 'idle' && (
              <div className="forgot-pass-container">
                <h3 className="section-title">Reset Password</h3>
                {forgotPasswordState === 'input-email' && (
                  <>
                    <div className="input-group-premium">
                      <label>Enter your email</label>
                      <input type="email" value={resetEmail} onChange={(e) => setResetEmail(e.target.value)} placeholder="Email" />
                    </div>
                    <button type="button" className="btn-auth-primary" onClick={handleSendOtp}>Send Reset Code</button>
                    <button type="button" className="btn-ghost" onClick={() => setForgotPasswordState('idle')}>Back to Login</button>
                  </>
                )}
                {forgotPasswordState === 'otp-sent' && (
                  <>
                    <div className="input-group-premium">
                      <label>Enter 6-digit Code</label>
                      <input type="text" value={resetOtp} onChange={(e) => setResetOtp(e.target.value)} placeholder="123456" maxLength="6" className="otp-input-center" />
                    </div>
                    <button type="button" className="btn-auth-primary" onClick={handleVerifyOtp}>Verify Code</button>
                  </>
                )}
                {forgotPasswordState === 'verified' && (
                  <>
                    <div className="input-group-premium">
                      <label>New Password</label>
                      <input type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} placeholder="Min 6 characters" />
                    </div>
                    <button type="button" className="btn-auth-primary" onClick={handleResetPassword}>Update Password</button>
                  </>
                )}
              </div>
            )}

            {/* Signup Form */}
            {activeTab === 'signup' && (
              <form onSubmit={handleSignupSubmit} className="premium-form">
                <div className="form-row-group">
                  <div className="input-group-premium">
                    <label>Full Name</label>
                    <input type="text" name="name" value={signupData.name} onChange={handleSignupChange} required placeholder="John Doe" />
                  </div>
                  <div className="input-group-premium">
                    <label>Mobile Number</label>
                    <input type="tel" name="mobile" value={signupData.mobile} onChange={handleSignupChange} required placeholder="10-digit mobile number" pattern="[0-9]{10}" />
                  </div>
                </div>
                <div className="input-group-premium">
                  <label>Email</label>
                  <input type="email" name="email" value={signupData.email} onChange={handleSignupChange} required placeholder="email@example.com" />
                </div>
                <div className="form-row-group">
                  <div className="input-group-premium">
                    <label>Create Password</label>
                    <input type="password" name="password" value={signupData.password} onChange={handleSignupChange} required placeholder="At least 6 characters" />
                  </div>
                  <div className="input-group-premium">
                    <label>Confirm Password</label>
                    <input type="password" name="confirmPassword" value={signupData.confirmPassword} onChange={handleSignupChange} required placeholder="Re-type password" />
                  </div>
                </div>
                <div className="form-row-group">
                  <div className="input-group-premium">
                    <label>4-Digit Security PIN</label>
                    <input type="password" name="securityPin" value={signupData.securityPin} onChange={handleSignupChange} required placeholder="••••" maxLength="4" pattern="\d{4}" inputMode="numeric" />
                  </div>
                  <div className="input-group-premium">
                    <label>Confirm PIN</label>
                    <input type="password" name="confirmSecurityPin" value={signupData.confirmSecurityPin} onChange={handleSignupChange} required placeholder="••••" maxLength="4" pattern="\d{4}" inputMode="numeric" />
                  </div>
                </div>
                <p className="pin-hint">🔒 This PIN is required for publishing listings, placing bids, and responding to bids.</p>
                <button type="submit" className="btn-auth-primary" disabled={loading}>
                  {loading ? 'Creating Account...' : 'Get Started'}
                </button>
              </form>
            )}
          </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Login;